import axios, { AxiosInstance } from "axios";

// Base URL - Use API Gateway like all other services
// The API Gateway will proxy /api/riders/* to rider-service on port 4005
const API_BASE_URL =
  import.meta.env.VITE_RIDER_API_URL || "http://localhost:8000/api";

class VendorService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Vendor service error:",
          error.response?.data || error.message
        );
        return Promise.reject(error);
      }
    );
  }

  // Get all vendors with filters
  async getVendors(params?: {
    page?: number;
    limit?: number;
    status?: string;
    city?: string;
    search?: string;
    isVerified?: boolean;
  }) {
    // API Gateway routes /api/riders/* to rider-service:4005/api/v1/*
    // So /api/riders/vendors becomes rider-service:4005/api/v1/vendors
    const response = await this.api.get("/riders/vendors", { params });
    return response.data;
  }

  // Get vendor by ID
  async getVendorById(id: string) {
    const response = await this.api.get(`/riders/vendors/${id}`);
    return response.data;
  }

  // Create new vendor
  async createVendor(data: {
    name: string;
    phone: string;
    email?: string;
    city?: string;
    state?: string;
    pincode?: string;
    address?: string;
    aadhaar?: string;
    pan?: string;
    operatingCities?: string[];
    territoryAssigned?: string;
  }) {
    const response = await this.api.post("/riders/vendors", data);
    return response.data;
  }

  // Update vendor
  async updateVendor(
    id: string,
    data: {
      name?: string;
      email?: string;
      city?: string;
      state?: string;
      pincode?: string;
      address?: string;
      operatingCities?: string[];
      territoryAssigned?: string;
      notes?: string;
    }
  ) {
    const response = await this.api.put(`/riders/vendors/${id}`, data);
    return response.data;
  }

  // Verify vendor (Admin action)
  async verifyVendor(id: string, verifiedBy: string) {
    const response = await this.api.put(`/riders/vendors/${id}/verify`, {
      verifiedBy,
    });
    return response.data;
  }

  // Suspend vendor (Admin action)
  async suspendVendor(id: string, reason: string, suspendedBy: string) {
    const response = await this.api.put(`/riders/vendors/${id}/suspend`, {
      reason,
      suspendedBy,
    });
    return response.data;
  }

  // Activate vendor
  async activateVendor(id: string) {
    const response = await this.api.put(`/riders/vendors/${id}/activate`);
    return response.data;
  }

  // Get vendor performance metrics
  async getVendorPerformance(id: string) {
    const response = await this.api.get(`/riders/vendors/${id}/performance`);
    return response.data;
  }

  // Update vendor bank details
  async updateBankDetails(
    id: string,
    data: {
      bankAccountHolder: string;
      bankAccountNumber: string;
      bankIfscCode: string;
      bankName: string;
      bankBranch?: string;
      bankProofUrl?: string;
    }
  ) {
    const response = await this.api.put(
      `/riders/vendors/${id}/bank-details`,
      data
    );
    return response.data;
  }

  // Refresh vendor metrics
  async updateVendorMetrics(id: string) {
    const response = await this.api.post(
      `/riders/vendors/${id}/update-metrics`
    );
    return response.data;
  }

  // Refresh all vendor metrics (Admin)
  async updateAllVendorMetrics() {
    const response = await this.api.post("/riders/vendors/metrics/update-all");
    return response.data;
  }

  // Upload vendor document (Aadhaar or PAN)
  async uploadDocument(
    vendorId: string,
    file: File,
    documentType: "aadhaar" | "pan",
    uploadedBy?: string
  ) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);
    if (uploadedBy) {
      formData.append("uploadedBy", uploadedBy);
    }

    const response = await this.api.post(
      `/riders/vendors/${vendorId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 60000, // 60 seconds for file upload
      }
    );
    return response.data;
  }
}

export const vendorService = new VendorService();
export default vendorService;
